<script lang="ts">
  /*
   * Preview.svelte — the rendered-markdown surface.
   *
   * Calls the shared render() pipeline (lib/render/index.ts) to turn `source`
   * into sanitized HTML, then hydrates the lazy bits (Mermaid + Shiki) once
   * the HTML lands in the DOM.
   *
   * The boundary that makes the HTML assignment below safe is upstream —
   * render() runs DOMPurify (isomorphic-dompurify) on every output before
   * returning. Allowlist + threat model are documented in
   * src/lib/render/sanitize.ts. The share-URL flow assumes every document is
   * attacker-controlled; DOMPurify is the sanitizer chokepoint and Mermaid
   * runs at securityLevel:'strict' so labels can't smuggle markup.
   *
   * Hydration order is load-bearing:
   *   1. assign HTML into host  (Mermaid placeholders + Shiki language
   *      classes survive the sanitize pass and need to be in the DOM
   *      before the walkers run)
   *   2. void hydrateMermaid(host, diagrams)  (.mermaid-placeholder)
   *   3. void hydrateCode(host)               (pre > code[class*=language-])
   * Both hydrators return Promises but we deliberately don't await — they
   * update the DOM asynchronously, which is fine for preview. The lazy
   * imports inside each mean a document with no diagrams pays zero
   * Mermaid bytes and a document with no fences pays zero Shiki bytes.
   */
  import { render, type RenderResult } from '../lib/render'
  import { hydrateMermaid, type MermaidTheme } from '../lib/render/mermaid'
  import { hydrateCode } from '../lib/render/code'
  import { primeMath } from '../lib/render/math'
  import { settings } from '../state/settings.svelte'

  type Props = {
    source: string
    zoom?: number
    wide?: boolean
    onRender?: (r: RenderResult) => void
    host?: HTMLElement | undefined
    scroller?: HTMLElement | undefined
  }

  let {
    source,
    zoom = 100,
    wide = false,
    onRender,
    host = $bindable(),
    scroller = $bindable(),
  }: Props = $props()

  // Derived — Svelte re-computes only when source changes. render() itself
  // is sync; Mermaid + Shiki engines are hydrated AFTER the HTML mounts.
  // mathTick bumps when KaTeX finishes its first lazy import, forcing a
  // re-render of math-bearing docs so the placeholder swap lands.
  let mathTick = $state(0)
  // Plain let (not $state) — used to gate the prime kick-off so a real TeX
  // syntax error (which legitimately renders a katex-error span) doesn't
  // cause an infinite re-render loop.
  let mathPrimedFor = ''
  const result = $derived.by(() => {
    void mathTick
    return render(source)
  })

  // Map app color mode → Mermaid theme (light = neutral, dark = dark).
  const mermaidTheme = $derived.by<MermaidTheme>(() => {
    const resolved =
      (typeof document !== 'undefined' && document.documentElement.dataset['mode']) || settings.mode
    return resolved === 'dark' ? 'dark' : 'neutral'
  })

  $effect(() => {
    if (!host) return
    // Read the derived value so the effect re-runs on source changes.
    const r = result
    // The HTML below has already passed through DOMPurify in render() —
    // see file-header note. Mermaid + Shiki hooks are walked next.
    host.innerHTML = r.html
    onRender?.(r)
    // Fire-and-forget; both handle their own per-block errors.
    // hydrateMermaid (P1.5) requires the per-render nonce so user-pasted
    // literal placeholder divs can't claim a hydration slot. theme (P1.10)
    // re-initializes Mermaid when the app theme changes.
    void hydrateMermaid(host, r.mermaidDiagrams, r.mermaidNonce, { theme: mermaidTheme })
    void hydrateCode(host)
    // If the source contained math and the first render used the
    // placeholder fallback (KaTeX wasn't cached yet), wait for the
    // lazy import and re-render so real KaTeX HTML lands. Subsequent
    // renders within this session render synchronously.
    //
    // Detection: katex-error WITHOUT katex (real KaTeX output adds class="katex"
    // even on parse errors). We also bump only once per source — mathPrimedFor
    // remembers the source we already kicked the prime for so a legitimate
    // TeX syntax error doesn't loop us.
    if (r.html.includes('katex-error') && !r.html.includes('class="katex"') && mathPrimedFor !== source) {
      mathPrimedFor = source
      void primeMath().then(() => {
        mathTick++
      })
    }
  })

  // Re-hydrate Mermaid when only light/dark mode changes (no source edit).
  $effect(() => {
    if (!host) return
    const theme = mermaidTheme
    const r = result
    // Read r.mermaidDiagrams via the captured derived; this effect will
    // re-run whenever either changes.
    if (r.mermaidDiagrams.length === 0) return
    void hydrateMermaid(host, r.mermaidDiagrams, r.mermaidNonce, { theme })
  })
</script>

<div class="preview-host" class:wide bind:this={scroller}>
  <article
    bind:this={host}
    class="prose"
    class:wide
    style="font-size: {zoom / 100}rem; max-width: {wide ? 'min(100%, 960px)' : 'var(--measure)'};"
  ></article>
</div>

<style>
  .preview-host {
    height: 100%;
    min-width: 0;
    overflow-x: auto;
    overflow-y: auto;
    background: var(--bg-surface);
    padding: var(--space-5);
  }

  .preview-host.wide {
    padding: var(--space-5) var(--space-6);
  }

  .prose {
    margin: 0 auto;
    line-height: var(--line-height);
    color: var(--text-primary);
    font-family: var(--font-body);
    min-width: 0;
    width: 100%;
    /*
     * Scoped type scale — em-based so the inline `font-size` set on this
     * element (driven by settings.previewZoom) scales every heading +
     * caption + caption together with body copy. The app-wide --text-*
     * tokens in tokens.css remain rem-based; these locals shadow them
     * only inside .prose. Settings drawer's "Text size" presets work
     * because of this scoping.
     */
    --text-xs: 0.75em;
    --text-sm: 0.875em;
    --text-base: 1em;
    --text-lg: 1.125em;
    --text-xl: 1.25em;
    --text-2xl: 1.5em;
    --text-3xl: 1.875em;
    --text-4xl: 2.25em;
  }

  .prose.wide {
    max-width: min(100%, 960px);
    overflow-x: auto;
  }

  :global(.prose h1),
  :global(.prose h2),
  :global(.prose h3),
  :global(.prose h4),
  :global(.prose h5),
  :global(.prose h6) {
    font-family: var(--font-body);
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.25;
    margin: var(--space-5) 0 var(--space-3);
  }

  :global(.prose h1) {
    font-size: var(--text-3xl);
    padding-bottom: var(--space-2);
    border-bottom: 1px solid var(--border-subtle);
  }
  :global(.prose h1:first-child) {
    margin-top: 0;
  }
  :global(.prose h2) {
    font-size: var(--text-2xl);
    padding-bottom: var(--space-2);
    border-bottom: 1px solid var(--border-subtle);
  }
  :global(.prose h3) {
    font-size: var(--text-xl);
  }
  :global(.prose h4) {
    font-size: var(--text-lg);
  }

  :global(.prose p) {
    margin: 0 0 var(--space-4);
  }

  :global(.prose a) {
    color: var(--accent);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  :global(.prose blockquote) {
    margin: var(--space-4) 0;
    padding: 0 var(--space-4);
    border-left: 4px solid var(--border-strong);
    color: var(--text-secondary);
  }
  :global(.prose blockquote p:last-child) {
    margin-bottom: 0;
  }

  :global(.prose aside[data-callout]) {
    --callout-color: var(--accent);
    margin: var(--space-4) 0;
    padding: var(--space-3) var(--space-4);
    border: 1px solid var(--border-subtle);
    border-left: 4px solid var(--callout-color);
    border-radius: var(--radius-sm);
    background: var(--bg-surface);
  }
  :global(.prose aside[data-callout] .callout-label) {
    display: block;
    font-size: var(--text-xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: var(--letter-spacing-mono-caps);
    color: var(--callout-color);
    margin-bottom: var(--space-2);
  }
  :global(.prose aside[data-callout='note']) { --callout-color: var(--callout-note); }
  :global(.prose aside[data-callout='tip']) { --callout-color: var(--callout-tip); }
  :global(.prose aside[data-callout='important']) { --callout-color: var(--callout-important); }
  :global(.prose aside[data-callout='warning']) { --callout-color: var(--callout-warning); }
  :global(.prose aside[data-callout='caution']) { --callout-color: var(--callout-caution); }

  :global(.prose strong) {
    color: var(--text-primary);
    font-weight: 600;
  }

  :global(.prose code) {
    background: var(--code-bg);
    color: var(--code-text);
    padding: 0.15em 0.35em;
    border-radius: var(--radius-sm);
    font-family: var(--font-mono);
    font-size: 0.9em;
  }

  :global(.prose a code) {
    color: var(--accent);
  }

  :global(.prose pre) {
    background: var(--code-block-bg);
    color: var(--code-block-text);
    padding: var(--space-4);
    border-radius: var(--radius-md);
    border: 1px solid var(--border-subtle);
    overflow-x: auto;
    margin: var(--space-4) 0;
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    line-height: 1.5;
  }
  :global(.prose pre code) {
    background: transparent;
    padding: 0;
    font-size: inherit;
  }

  :global(.prose ul),
  :global(.prose ol) {
    margin: 0 0 var(--space-4);
    padding-left: 1.5em;
  }
  :global(.prose li) {
    margin: var(--space-1) 0;
  }

  /* Wide tables (lots of columns, long content) used to crush one column
     to fit the prose measure, breaking unbreakable strings mid-word —
     e.g. "printurr-server" turning into "printurr-serve" + "r" on the
     next line. Two fixes work together:
       1. The render pipeline wraps every table in <div class="table-scroll">
          so wide tables scroll horizontally inside the prose width instead
          of column-crushing.
       2. Cells use overflow-wrap: break-word (not "anywhere") and inline
          code drops the old word-break: break-all, so words only break at
          natural boundaries. Combined with #1, anything that genuinely
          doesn't fit triggers a scrollbar instead of mid-word breaks. */
  :global(.prose .table-scroll) {
    overflow-x: auto;
    margin: var(--space-4) 0;
    border-radius: var(--radius-sm);
  }
  :global(.prose .table-scroll table) {
    margin: 0;
  }
  :global(.prose table) {
    border-collapse: collapse;
    width: 100%;
    margin: var(--space-4) 0;
    font-size: var(--text-sm);
    table-layout: auto;
  }
  :global(.prose th),
  :global(.prose td) {
    border: 1px solid var(--border-subtle);
    padding: var(--space-2) var(--space-3);
    text-align: left;
    vertical-align: top;
    overflow-wrap: break-word;
    word-break: normal;
  }
  :global(.prose th) {
    background: var(--bg-sunken);
    font-weight: 600;
    white-space: nowrap;
  }
  :global(.prose td code) {
    white-space: normal;
    overflow-wrap: break-word;
    word-break: normal;
  }

  :global(.prose img) {
    max-width: 100%;
    height: auto;
    border-radius: var(--radius-sm);
  }

  :global(.prose hr) {
    border: none;
    border-top: 1px solid var(--border-subtle);
    margin: var(--space-5) 0;
  }

  :global(.prose .mermaid-placeholder) {
    display: flex;
    justify-content: center;
    margin: var(--space-4) 0;
    padding: var(--space-3);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    background: var(--bg-surface);
  }

  @media (max-width: 768px) {
    .preview-host {
      padding: var(--space-4);
    }
  }
</style>
